import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { FiltersContext, buildQuery } from "./_shared";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, Target, UserCircle, Clapperboard,
  FileText, BarChart3, Building2, RotateCcw, SlidersHorizontal,
} from "lucide-react";

const NAV = [
  { to: "/analytics", label: "Dashboard Overview", icon: LayoutDashboard, end: true },
  { to: "/analytics/batches", label: "Batch Analytics", icon: Users },
  { to: "/analytics/competencies", label: "Competency Analytics", icon: Target },
  { to: "/analytics/participants", label: "Participant Reports", icon: UserCircle },
  { to: "/analytics/scenarios", label: "Scenario Analytics", icon: Clapperboard },
  { to: "/analytics/debrief", label: "Facilitator Debrief", icon: FileText },
  { to: "/analytics/benchmark", label: "Benchmark Reports", icon: BarChart3 },
  { to: "/analytics/executive", label: "Executive Dashboard", icon: Building2 },
];

const FILTER_DEFS = [
  { key: "company_id", label: "Organization", opt: "organizations", map: (o) => ({ value: o.id, label: o.name }) },
  { key: "batch_id", label: "Batch", opt: "batches", map: (o) => ({ value: o.id, label: o.name }) },
  { key: "department", label: "Department", opt: "departments", map: (o) => ({ value: o, label: o }) },
  { key: "role", label: "Role", opt: "roles", map: (o) => ({ value: o, label: o }) },
  { key: "facilitator", label: "Facilitator", opt: "facilitators", map: (o) => ({ value: o.id, label: o.name }) },
  { key: "program", label: "Program", opt: "programs", map: (o) => ({ value: o, label: o }) },
];

const EMPTY = { company_id: "all", batch_id: "all", department: "all", role: "all", facilitator: "all", program: "all" };

export default function AnalyticsLayout() {
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState(EMPTY);
  const location = useLocation();

  useEffect(() => {
    api.get("/analytics/filters").then((r) => setOptions(r.data)).catch(() => setOptions({}));
  }, []);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const reset = () => setFilters(EMPTY);
  const query = useMemo(() => buildQuery(filters), [filters]);
  const activeCount = Object.values(filters).filter((v) => v && v !== "all").length;

  const ctx = { filters, setFilter, reset, query, options };

  return (
    <FiltersContext.Provider value={ctx}>
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
          {/* Sidebar */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-24">
              <div className="text-[11px] uppercase tracking-[0.14em] text-stone-500 font-medium px-3 mb-2">
                Analytics & Reporting
              </div>
              <nav className="space-y-0.5" data-testid="analytics-sidebar">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end}
                    data-testid={`nav-${n.to.split("/").pop() || "overview"}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                        isActive
                          ? "bg-[#b81f24] text-white font-medium shadow-sm"
                          : "text-stone-600 hover:bg-stone-100"
                      }`
                    }>
                    <n.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                    {n.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main */}
          <main className="flex-1 min-w-0">
            {/* Filter bar */}
            <div className="bg-white rounded-2xl border border-stone-200/80 p-4 mb-6" data-testid="filter-bar">
              <div className="flex items-center gap-2 mb-3">
                <SlidersHorizontal className="w-4 h-4 text-stone-500" strokeWidth={1.75} />
                <span className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Filters</span>
                {activeCount > 0 && (
                  <span className="text-[11px] bg-[#b81f24] text-white rounded-full px-2 py-0.5">{activeCount} active</span>
                )}
                <div className="flex-1" />
                <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-filters-btn"
                  className="text-stone-500 hover:text-stone-800 h-8">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
                </Button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {FILTER_DEFS.map((fd) => {
                  const list = (options?.[fd.opt] || []).map(fd.map);
                  return (
                    <div key={fd.key}>
                      <label className="text-[11px] text-stone-500 mb-1 block">{fd.label}</label>
                      <Select value={filters[fd.key]} onValueChange={(v) => setFilter(fd.key, v)}>
                        <SelectTrigger data-testid={`filter-${fd.key}`}
                          className="h-9 text-sm border-stone-300 bg-stone-50/60 rounded-lg">
                          <SelectValue placeholder="All" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="all">All</SelectItem>
                          {list.map((o) => (
                            <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>

            <Outlet />
          </main>
        </div>
      </div>
    </FiltersContext.Provider>
  );
}
